/**
 * Service Questionnaire Page
 * 
 * Beautiful AI-powered questionnaire for immigration services:
 * - Real Estate
 * - Employment
 * - English Academy
 * 
 * Features:
 * - Progress visualization sidebar
 * - Conversational AI chat
 * - Voice input with Whisper
 * - File upload with extraction
 * - Auto-focus input
 * - Modern dark theme design
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import styled, { keyframes, css } from 'styled-components';
import LogoLoader from '../LogoLoader';
import axios from 'axios';
import { IoSend, IoCheckmarkCircle, IoCloseCircle, IoHomeOutline, IoBriefcaseOutline, IoBookOutline, IoMic, IoStop, IoAttach } from 'react-icons/io5';
import { FaUser, FaRobot, FaFile, FaFilePdf, FaFileWord, FaFileImage } from 'react-icons/fa';
import { HiOutlineSparkles } from 'react-icons/hi';
import DOMPurify from 'dompurify';

// Maven Platform Server URL (Expert Server - where questionnaire API lives)
const MAVEN_API_BASE = process.env.REACT_APP_MAVEN_SERVER || 'https://sea-lion-app-7wbpx.ondigitalocean.app';

// ============================================
// ANIMATIONS
// ============================================

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to { opacity: 1; transform: translateY(0); }
`;

const pulse = keyframes`
  0%, 100% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.1); opacity: 0.8; }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-8px); }
`;

const recording = keyframes`
  0%, 100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(229, 72, 77, 0.4); }
  50% { transform: scale(1.05); box-shadow: 0 0 0 10px rgba(229, 72, 77, 0); }
`;

// ============================================
// SERVICE THEME CONFIG
// ============================================

const SERVICE_THEMES = {
  real_estate: {
    icon: '🏠',
    name: 'Real Estate',
    description: 'Find your perfect home in Canada',
    primaryColor: '#335cf4',
    secondaryColor: '#2d5bed',
    accentGlow: 'rgba(51, 92, 244, 0.4)',
    gradient: 'linear-gradient(135deg, #335cf4 0%, #2d5bed 100%)',
    bgGradient: 'linear-gradient(135deg, #f1f6fb 0%, #ffffff 50%, #f9fbff 100%)',
    IconComponent: IoHomeOutline
  },
  employment: {
    icon: '💼',
    name: 'Employment',
    description: 'Build your career profile',
    primaryColor: '#ef9b61',
    secondaryColor: '#e07b3c',
    accentGlow: 'rgba(239, 155, 97, 0.4)',
    gradient: 'linear-gradient(135deg, #ef9b61 0%, #e07b3c 100%)',
    bgGradient: 'linear-gradient(135deg, #fbf5f1 0%, #faf4f2 50%, #fbf4f1 100%)',
    IconComponent: IoBriefcaseOutline
  },
  english_academy: {
    icon: '📚',
    name: 'English Academy',
    description: 'Improve your language skills',
    primaryColor: '#885de8',
    secondaryColor: '#7955cd',
    accentGlow: 'rgba(136, 93, 232, 0.4)',
    gradient: 'linear-gradient(135deg, #885de8 0%, #7955cd 100%)',
    bgGradient: 'linear-gradient(135deg, #f5f2fa 0%, #ffffff 50%, #f5f1fb 100%)',
    IconComponent: IoBookOutline
  }
};

// ============================================
// STYLED COMPONENTS
// ============================================

const PageWrapper = styled.div`
  --primary-color: ${props => props.theme?.primaryColor || '#335cf4'};
  --secondary-color: ${props => props.theme?.secondaryColor || '#2d5bed'};
  --accent-glow: ${props => props.theme?.accentGlow || 'rgba(51, 92, 244, 0.4)'};
  
  height: 100vh;
  width: 100vw;
  overflow: hidden;
  /* Keep default gray-blue background - does NOT change with theme */
  background: linear-gradient(135deg, #f9fbff 0%, #ffffff 50%, #f9fbff 100%);
  display: flex;
  font-family: 'Inter', 'Segoe UI', -apple-system, BlinkMacSystemFont, Arial, sans-serif;
`;

const Sidebar = styled.div`
  width: 340px;
  height: 100%;
  overflow-y: auto;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.9) 100%);
  border-right: 1px solid rgba(var(--primary-color), 0.15);
  padding: 32px 24px;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  
  &::-webkit-scrollbar { width: 0; }
  
  @media (max-width: 900px) { display: none; }
`;

const ServiceLogo = styled.div`
  width: 72px;
  height: 72px;
  background: ${props => props.theme?.gradient};
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
  box-shadow: 0 8px 32px ${props => props.theme?.accentGlow};
  font-size: 36px;
  animation: ${float} 3s ease-in-out infinite;
`;

const ServiceName = styled.h1`
  font-size: 24px;
  font-weight: 700;
  color: #192947;
  margin: 0 0 8px 0;
  background: ${props => props.theme?.gradient || 'linear-gradient(135deg, #fff 0%, #e6ecf6 100%)'};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const ServiceSubtitle = styled.p`
  font-size: 14px;
  color: #455776;
  margin: 0 0 32px 0;
  line-height: 1.5;
`;

const ProgressSection = styled.div`
  margin-bottom: 32px;
`;

const ProgressLabel = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: #65748d;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 16px;
`;

const CircularProgress = styled.div`
  position: relative;
  width: 160px;
  height: 160px;
  margin: 0 auto 20px;
`;

const CircleSVG = styled.svg`
  transform: rotate(-90deg);
  width: 160px;
  height: 160px;
`;

const CircleBackground = styled.circle`
  fill: none;
  stroke: #65748d;
  stroke-width: 10;
`;

const CircleProgress = styled.circle`
  fill: none;
  stroke: url(#progressGradient);
  stroke-width: 10;
  stroke-linecap: round;
  stroke-dasharray: 440;
  stroke-dashoffset: ${props => 440 - (440 * props.progress) / 100};
  transition: stroke-dashoffset 0.6s ease;
  filter: drop-shadow(0 0 8px ${props => props.theme?.accentGlow});
`;

const ProgressCenter = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
`;

const ProgressPercent = styled.div`
  font-size: 36px;
  font-weight: 700;
  color: ${props => props.theme?.primaryColor || '#335cf4'};
  line-height: 1;
`;

const ProgressText = styled.div`
  font-size: 12px;
  color: #65748d;
  margin-top: 4px;
`;

const DataSection = styled.div`
  margin-top: auto;
`;

const DataLabel = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: #65748d;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 12px;
`;

const DataItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 12px 0;
  border-bottom: 1px solid #dce5f4;
  animation: ${fadeIn} 0.3s ease-out;
  
  &:last-child { border-bottom: none; }
`;

const DataKey = styled.span`
  font-size: 12px;
  color: #65748d;
  text-transform: capitalize;
`;

const DataValue = styled.span`
  font-size: 13px;
  color: #33445e;
  font-weight: 500;
  text-align: right;
  max-width: 60%;
  word-break: break-word;
`;

const ChatContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding: 0;
`;

const ChatHeader = styled.div`
  padding: 20px 32px;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.9) 100%);
  border-bottom: 1px solid #dce5f4;
  display: flex;
  align-items: center;
  gap: 16px;
  backdrop-filter: blur(10px);
  
  @media (max-width: 900px) {
    padding: 14px 12px;
    gap: 12px;
  }
`;

const ChatHeaderIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 14px;
  background: ${props => props.theme?.gradient};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  box-shadow: 0 4px 16px ${props => props.theme?.accentGlow};
  
  @media (max-width: 900px) {
    width: 40px;
    height: 40px;
    border-radius: 12px;
    font-size: 20px;
  }
`;

const ChatHeaderInfo = styled.div`
  flex: 1;
`;

const ChatHeaderTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: #192947;
  margin: 0 0 4px 0;
`;

const ChatHeaderSubtitle = styled.p`
  font-size: 13px;
  color: #65748d;
  margin: 0;
`;

const MessagesContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 24px 32px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  
  &::-webkit-scrollbar { width: 6px; }
  &::-webkit-scrollbar-track { background: transparent; }
  &::-webkit-scrollbar-thumb { background: rgba(101, 116, 141, 0.08); border-radius: 3px; }
  
  @media (max-width: 900px) {
    padding: 16px 12px;
    gap: 16px;
  }
`;

const MessageWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 14px;
  animation: ${fadeIn} 0.4s ease-out;
  ${props => props.isUser && css`flex-direction: row-reverse;`}
`;

const MessageAvatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 12px;
  background: ${props => props.isUser 
    ? 'linear-gradient(135deg, #e6ecf6 0%, #edf2ff 100%)' 
    : props.theme?.gradient};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.isUser ? '#192947' : '#ffffff'};
  font-size: 18px;
  flex-shrink: 0;
  box-shadow: ${props => props.isUser 
    ? '0 4px 12px rgba(39, 68, 131, 0.07)' 
    : `0 4px 12px ${props.theme?.accentGlow}`};
`;

const MessageBubble = styled.div`
  max-width: 70%;
  padding: 16px 20px;
  border-radius: 18px;
  font-size: 14px;
  line-height: 1.6;
  color: #33445e;
  white-space: pre-wrap;
  word-wrap: break-word;
  overflow-wrap: break-word;
  
  ${props => props.isUser ? css`
    background: linear-gradient(135deg, rgba(101, 116, 141, 0.36) 0%, rgba(255, 255, 255, 0.95) 100%);
    border: 1px solid #dce5f4;
    border-bottom-right-radius: 6px;
  ` : css`
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.95) 100%);
    border: 1px solid ${props.theme?.primaryColor}33;
    border-bottom-left-radius: 6px;
  `}
  
  /* HTML content styling */
  p {
    margin: 0 0 12px 0;
    white-space: pre-wrap;
    &:last-child {
      margin-bottom: 0;
    }
  }
  
  strong, b {
    font-weight: 600;
    color: ${props => props.isUser ? '#192947' : props.theme?.primaryColor || '#2c64da'};
  }
  
  em, i {
    font-style: italic;
    opacity: 0.9;
  }
  
  ul, ol {
    margin: 12px 0;
    padding-left: 24px;
  }
  
  li {
    margin: 8px 0;
    line-height: 1.5;
  }
  
  a {
    color: ${props => props.theme?.primaryColor || '#2c64da'};
    text-decoration: underline;
    &:hover {
      opacity: 0.8;
    }
  }
  
  h1, h2, h3, h4 {
    margin: 16px 0 8px 0;
    font-weight: 600;
    color: #192947;
    &:first-child {
      margin-top: 0;
    }
  }
  
  h1 { font-size: 1.4em; }
  h2 { font-size: 1.25em; }
  h3 { font-size: 1.1em; }
  h4 { font-size: 1em; }
  
  /* Table styling for formatted data */
  table {
    width: 100%;
    border-collapse: collapse;
    margin: 12px 0;
    font-size: 13px;
  }
  
  th, td {
    padding: 8px 12px;
    text-align: left;
    border: 1px solid #dce5f4;
  }
  
  th {
    background: rgba(101, 116, 141, 0.04);
    font-weight: 600;
    color: ${props => props.theme?.primaryColor || '#2c64da'};
  }
  
  tr:nth-child(even) {
    background: rgba(101, 116, 141, 0.02);
  }
  
  /* Code and pre blocks */
  pre, code {
    white-space: pre-wrap;
    font-family: 'Monaco', 'Menlo', 'Consolas', monospace;
    font-size: 13px;
  }
  
  pre {
    background: rgba(25, 41, 71, 0.06);
    padding: 12px;
    border-radius: 8px;
    overflow-x: auto;
    margin: 12px 0;
  }
  
  code {
    background: rgba(101, 116, 141, 0.06);
    padding: 2px 6px;
    border-radius: 4px;
  }
  
  /* Horizontal rules */
  hr {
    border: none;
    border-top: 1px solid #dce5f4;
    margin: 16px 0;
  }
  
  /* Blockquotes */
  blockquote {
    border-left: 3px solid ${props => props.theme?.primaryColor || '#335cf4'};
    padding-left: 16px;
    margin: 12px 0;
    opacity: 0.9;
  }
`;

const TypingIndicator = styled.div`
  display: flex;
  gap: 4px;
  padding: 16px 20px;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.95) 100%);
  border: 1px solid ${props => props.theme?.primaryColor}33;
  border-radius: 18px;
  border-bottom-left-radius: 6px;
  width: fit-content;
  max-width: 100%;
`;

const InputArea = styled.div`
  padding: 16px 20px 20px;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.9) 0%, rgba(255, 255, 255, 0.95) 100%);
  border-top: 1px solid #dce5f4;
  backdrop-filter: blur(10px);
  
  @media (max-width: 900px) {
    padding: 10px 6px 12px;
  }
`;

const InputWrapper = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 12px;
  /* Keep default gray-blue background - does NOT change with theme */
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(255, 255, 255, 0.9) 100%);
  border: 1px solid ${props => props.focused ? props.theme?.primaryColor : '#dce5f4'};
  border-radius: 16px;
  padding: 12px 16px;
  transition: all 0.2s ease;
  
  ${props => props.focused && css`box-shadow: 0 0 0 3px ${props.theme?.accentGlow};`}
  
  @media (max-width: 900px) {
    gap: 6px;
    padding: 6px 8px;
    border-radius: 14px;
  }
`;

const TextInput = styled.textarea`
  flex: 1;
  background: transparent;
  border: none;
  color: #33445e;
  font-size: 15px;
  font-family: inherit;
  resize: none;
  min-height: 24px;
  max-height: 120px;
  line-height: 1.5;
  min-width: 0;
  
  &::placeholder { color: #65748d; }
  &:focus { outline: none; }
  
  @media (max-width: 900px) {
    font-size: 14px;
    min-height: 20px;
  }
`;

const SendButton = styled.button`
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: ${props => props.disabled ? 'rgba(101, 116, 141, 0.2)' : props.theme?.gradient};
  border: none;
  color: ${props => props.disabled ? '#8f9db3' : '#ffffff'};
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  transition: all 0.2s ease;
  flex-shrink: 0;
  
  ${props => !props.disabled && css`
    &:hover { transform: scale(1.05); box-shadow: 0 4px 16px ${props.theme?.accentGlow}; }
  `}
  
  @media (max-width: 900px) {
    width: 36px;
    height: 36px;
    border-radius: 10px;
    font-size: 16px;
  }
`;

const ActionButton = styled.button`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: ${props => props.isRecording 
    ? 'linear-gradient(135deg, #e5484d 0%, #dc2f2f 100%)'
    : 'rgba(101, 116, 141, 0.12)'};
  border: 1px solid ${props => props.isRecording 
    ? 'rgba(229, 72, 77, 0.5)'
    : '#dce5f4'};
  color: ${props => props.isRecording ? '#192947' : '#455776'};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  transition: all 0.2s ease;
  flex-shrink: 0;
  
  ${props => props.isRecording && css`animation: ${recording} 1.5s ease-in-out infinite;`}
  
  &:hover {
    background: ${props => props.isRecording ? 'linear-gradient(135deg, #dc2f2f 0%, #b42323 100%)' : 'rgba(101, 116, 141, 0.2)'};
    color: ${props => props.isRecording ? '#192947' : '#33445e'};
  }
  
  &:disabled { opacity: 0.5; cursor: not-allowed; }
  
  @media (max-width: 900px) {
    width: 32px;
    height: 32px;
    border-radius: 8px;
    font-size: 14px;
  }
`;

const RecordingIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  background: linear-gradient(135deg, rgba(229, 72, 77, 0.15) 0%, rgba(220, 47, 47, 0.2) 100%);
  border: 1px solid rgba(229, 72, 77, 0.3);
  border-radius: 20px;
  font-size: 12px;
  color: #d63c3c;
  font-weight: 500;
  margin-bottom: 12px;
`;

const RecordingDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #e5484d;
  animation: ${pulse} 1s ease-in-out infinite;
`;

const TranscribingStatus = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  background: linear-gradient(135deg, rgba(51, 92, 244, 0.15) 0%, rgba(45, 91, 237, 0.2) 100%);
  border: 1px solid rgba(51, 92, 244, 0.3);
  border-radius: 20px;
  font-size: 12px;
  color: #335cf4;
  font-weight: 500;
  margin-bottom: 12px;
`;

const HiddenFileInput = styled.input`
  display: none;
`;

const FilePreviewContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 12px;
`;

const FilePreviewItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: rgba(101, 116, 141, 0.12);
  border: 1px solid #dce5f4;
  border-radius: 8px;
  font-size: 12px;
  color: #33445e;
`;

const FileIcon = styled.span`
  font-size: 16px;
  color: ${props => props.theme?.primaryColor || '#335cf4'};
`;

const FileName = styled.span`
  max-width: 150px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const RemoveFileButton = styled.button`
  background: none;
  border: none;
  color: #65748d;
  cursor: pointer;
  padding: 2px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  
  &:hover { color: #d63c3c; background: rgba(229, 72, 77, 0.1); }
`;

const InputActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  
  @media (max-width: 900px) {
    gap: 4px;
  }
`;

const UploadedDocumentsSection = styled.div`
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #dce5f4;
`;

const UploadedDocItem = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 0;
  border-bottom: 1px solid #dce5f4;
  animation: ${fadeIn} 0.3s ease-out;
  
  &:last-child { border-bottom: none; }
`;

const DocIcon = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: ${props => props.theme?.gradient || 'linear-gradient(135deg, #335cf4 0%, #2d5bed 100%)'};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 14px;
`;

const DocInfo = styled.div`
  flex: 1;
`;

const DocName = styled.div`
  font-size: 12px;
  color: #33445e;
  font-weight: 500;
`;

const DocMeta = styled.div`
  font-size: 10px;
  color: #65748d;
`;

// File uploading overlay
const UploadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.95);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 100;
  animation: ${fadeIn} 0.3s ease-out;
  border-radius: inherit;
`;

const UploadingSubtitle = styled.div`
  font-size: 14px;
  color: #455776;
  margin-bottom: 20px;
`;

const CompleteScreen = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  text-align: center;
  animation: ${fadeIn} 0.5s ease-out;
`;

const CompleteIcon = styled.div`
  width: 100px;
  height: 100px;
  border-radius: 50%;
  background: ${props => props.theme?.gradient};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 48px;
  margin-bottom: 24px;
  box-shadow: 0 8px 40px ${props => props.theme?.accentGlow};
  animation: ${pulse} 2s ease-in-out infinite;
`;

const CompleteTitle = styled.h2`
  font-size: 28px;
  font-weight: 700;
  color: #192947;
  margin: 0 0 12px 0;
`;

const CompleteSubtitle = styled.p`
  font-size: 16px;
  color: #455776;
  margin: 0 0 32px 0;
  max-width: 400px;
  line-height: 1.6;
`;

const CompleteButton = styled.button`
  background: ${props => props.theme?.gradient};
  color: #ffffff;
  border: none;
  padding: 14px 32px;
  border-radius: 12px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover { transform: translateY(-2px); box-shadow: 0 8px 24px ${props => props.theme?.accentGlow}; }
`;

const LoadingContainer = styled.div`
  height: 100vh;
  width: 100vw;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #f9fbff 0%, #ffffff 50%, #f9fbff 100%);
  gap: 20px;
`;

// Generating Report Animation
const GeneratingContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  text-align: center;
  animation: ${fadeIn} 0.5s ease-out;
`;

const GeneratingSubtitle = styled.p`
  font-size: 15px;
  color: #455776;
  margin: 0 0 32px 0;
  max-width: 400px;
  line-height: 1.6;
`;

const GeneratingSteps = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 24px;
`;

const GeneratingStep = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 14px;
  color: ${props => props.active ? '#192947' : '#65748d'};
  transition: all 0.3s ease;
  
  svg {
    font-size: 18px;
    color: ${props => props.completed ? '#0f8f63' : props.active ? props.theme?.primaryColor : '#65748d'};
  }
`;

const ErrorContainer = styled.div`
  height: 100vh;
  width: 100vw;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #f9fbff 0%, #ffffff 50%, #f9fbff 100%);
  text-align: center;
  padding: 40px;
`;

const ErrorIcon = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: rgba(229, 72, 77, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 40px;
  color: #d63c3c;
  margin-bottom: 24px;
`;

const ErrorTitle = styled.h2`
  font-size: 24px;
  font-weight: 700;
  color: #192947;
  margin: 0 0 12px 0;
`;

const ErrorText = styled.p`
  font-size: 14px;
  color: #65748d;
  margin: 0;
`;

// ============================================
// MAIN COMPONENT
// ============================================

const ServiceQuestionnairePage = () => {
  const { sessionId } = useParams();
  const [session, setSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [error, setError] = useState(null);
  const [isComplete, setIsComplete] = useState(false);
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);
  const [inputFocused, setInputFocused] = useState(false);
  const [collectedData, setCollectedData] = useState({});
  const [progress, setProgress] = useState({ current: 0, total: 25, percent: 0 });
  
  // Voice recording state
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [transcriptionStatus, setTranscriptionStatus] = useState('');
  const [mediaRecorder, setMediaRecorder] = useState(null);
  
  // File upload state
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadedDocuments, setUploadedDocuments] = useState([]);
  const [isUploadingFile, setIsUploadingFile] = useState(false);
  
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);
  const recordingTimerRef = useRef(null);
  
  const theme = session?.serviceType ? SERVICE_THEMES[session.serviceType] : SERVICE_THEMES.real_estate;

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  useEffect(() => {
    if (!isTyping && !isSending && !isRecording && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isTyping, isSending, isRecording]);

  useEffect(() => {
    if (isRecording) {
      recordingTimerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
    } else {
      clearInterval(recordingTimerRef.current);
      setRecordingTime(0);
    }
    return () => clearInterval(recordingTimerRef.current);
  }, [isRecording]);

  // Parse progress from assistant messages
  useEffect(() => {
    if (messages.length === 0) return;
    
    // Look for [PROGRESS:X/Y] pattern in the last assistant message
    const lastAssistantMessage = [...messages].reverse().find(m => m.role === 'assistant');
    if (!lastAssistantMessage) return;
    
    const progressMatch = lastAssistantMessage.content.match(/\[PROGRESS:(\d+)\/(\d+)\]/);
    if (progressMatch) {
      const current = parseInt(progressMatch[1], 10);
      const total = parseInt(progressMatch[2], 10);
      const percent = Math.round((current / total) * 100);
      setProgress({ current, total, percent });
    }
  }, [messages]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const toggleRecording = async () => {
    if (isRecording) {
      if (mediaRecorder && mediaRecorder.state !== 'inactive') {
        mediaRecorder.stop();
      }
      setIsRecording(false);
    } else {
      try {
        setTranscriptionStatus('');
        
        const stream = await navigator.mediaDevices.getUserMedia({ 
          audio: { echoCancellation: true, noiseSuppression: true } 
        });
        
        const mimeType = MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : 'audio/mp4';
        const recorder = new MediaRecorder(stream, { mimeType });
        const chunks = [];
        
        recorder.ondataavailable = (e) => {
          if (e.data.size > 0) chunks.push(e.data);
        };
        
        recorder.onstop = async () => {
          stream.getTracks().forEach(track => track.stop());
          if (chunks.length > 0) {
            const audioBlob = new Blob(chunks, { type: mimeType });
            if (audioBlob.size > 1000) {
              await transcribeAudio(audioBlob);
            } else {
              setTranscriptionStatus('Recording too short');
              setTimeout(() => setTranscriptionStatus(''), 3000);
            }
          }
        };
        
        recorder.onerror = () => {
          setTranscriptionStatus('Recording error');
          setTimeout(() => setTranscriptionStatus(''), 3000);
          stream.getTracks().forEach(track => track.stop());
          setIsRecording(false);
        };
        
        setMediaRecorder(recorder);
        recorder.start(1000);
        setIsRecording(true);
      } catch (err) {
        console.error('Error starting recording:', err);
        setTranscriptionStatus('Microphone access denied');
        setTimeout(() => setTranscriptionStatus(''), 3000);
      }
    }
  };

  const transcribeAudio = async (audioBlob) => {
    try {
      setIsTranscribing(true);
      setTranscriptionStatus('Transcribing...');
      
      const formData = new FormData();
      formData.append('audio', audioBlob, 'recording.webm');
      
      const response = await fetch(`${MAVEN_API_BASE}/api/transcribe`, {
        method: 'POST',
        body: formData
      });
      
      if (!response.ok) throw new Error('Transcription failed');
      
      const data = await response.json();
      
      if (data.transcription && data.transcription.trim()) {
        setInputValue(prev => {
          const prefix = prev.trim() ? `${prev.trim()} ` : '';
          return `${prefix}${data.transcription.trim()}`;
        });
        setTranscriptionStatus('');
        setTimeout(() => inputRef.current?.focus(), 100);
      } else {
        setTranscriptionStatus('No speech detected');
        setTimeout(() => setTranscriptionStatus(''), 3000);
      }
    } catch (err) {
      console.error('Transcription error:', err);
      setTranscriptionStatus('Transcription failed');
      setTimeout(() => setTranscriptionStatus(''), 3000);
    } finally {
      setIsTranscribing(false);
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) setSelectedFiles(prev => [...prev, ...files]);
    e.target.value = '';
  };

  const removeSelectedFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const uploadAndProcessFiles = async () => {
    if (selectedFiles.length === 0) return null;
    
    setIsUploadingFile(true);
    
    try {
      const formData = new FormData();
      selectedFiles.forEach(file => formData.append('files', file));
      
      const uploadResponse = await axios.post(
        `${MAVEN_API_BASE}/service-questionnaire/sessions/${sessionId}/upload`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      
      if (uploadResponse.data.success) {
        const fileNames = selectedFiles.map(f => f.name);
        const newDocs = selectedFiles.map(file => ({
          name: file.name,
          size: file.size,
          type: file.type,
          uploadedAt: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        }));
        setUploadedDocuments(prev => [...prev, ...newDocs]);
        setSelectedFiles([]);
        return { 
          fileNames, 
          extractedData: uploadResponse.data.extractedData || {},
          aiResponse: uploadResponse.data.aiResponse || null
        };
      }
      return null;
    } catch (err) {
      console.error('File upload error:', err);
      return null;
    } finally {
      setIsUploadingFile(false);
    }
  };

  const getFileIcon = (fileName) => {
    const ext = fileName.split('.').pop().toLowerCase();
    switch (ext) {
      case 'pdf': return <FaFilePdf />;
      case 'doc': case 'docx': return <FaFileWord />;
      case 'jpg': case 'jpeg': case 'png': case 'gif': return <FaFileImage />;
      default: return <FaFile />;
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  useEffect(() => {
    const loadSession = async () => {
      try {
        setIsLoading(true);
        
        const sessionRes = await axios.get(`${MAVEN_API_BASE}/service-questionnaire/sessions/${sessionId}`);
        
        if (!sessionRes.data.success) {
          setError(sessionRes.data.error || 'Session not found');
          return;
        }

        setSession(sessionRes.data.session);

        if (sessionRes.data.session.status === 'completed') {
          setIsComplete(true);
          return;
        }

        const startRes = await axios.get(`${MAVEN_API_BASE}/service-questionnaire/sessions/${sessionId}/start`);
        
        if (startRes.data.success) {
          setSession(startRes.data.session);
          setMessages([{ role: 'assistant', content: startRes.data.message }]);
          if (startRes.data.session.existingUserData) {
            setCollectedData(startRes.data.session.existingUserData);
          }
        }
      } catch (err) {
        console.error('Error loading session:', err);
        setError(err.response?.data?.error || 'Failed to load questionnaire');
      } finally {
        setIsLoading(false);
      }
    };

    if (sessionId) loadSession();
  }, [sessionId]);

  const handleSendMessage = async () => {
    if ((!inputValue.trim() && selectedFiles.length === 0) || isSending) return;

    const userMessage = inputValue.trim();
    setInputValue('');
    setIsSending(true);
    
    let fileInfo = null;
    if (selectedFiles.length > 0) {
      fileInfo = await uploadAndProcessFiles();
      const fileMessage = fileInfo ? `📎 Uploaded: ${fileInfo.fileNames.join(', ')}` : '📎 Files uploaded';
      setMessages(prev => [...prev, { 
        role: 'user', 
        content: userMessage ? `${fileMessage}\n\n${userMessage}` : fileMessage,
        hasFiles: true,
        fileNames: fileInfo?.fileNames
      }]);
      
      // If file upload returned AI response, use it directly
      if (fileInfo?.aiResponse) {
        if (fileInfo.extractedData && Object.keys(fileInfo.extractedData).length > 0) {
          setCollectedData(prev => ({ ...prev, ...fileInfo.extractedData }));
        }
        setMessages(prev => [...prev, { role: 'assistant', content: fileInfo.aiResponse }]);
        setIsSending(false);
        return; // Don't make another API call - upload already handled it
      }
    } else if (userMessage) {
      setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    }
    
    // Only send to /message if there's a text message (files already handled above)
    if (!userMessage && fileInfo) {
      setIsSending(false);
      return;
    }
    
    setIsTyping(true);

    try {
      const response = await axios.post(
        `${MAVEN_API_BASE}/service-questionnaire/sessions/${sessionId}/message`,
        { 
          message: userMessage,
          hasFiles: false
        }
      );

      if (response.data.success) {
        setMessages(prev => [...prev, { role: 'assistant', content: response.data.message }]);
        
        if (response.data.extractedData && Object.keys(response.data.extractedData).length > 0) {
          setCollectedData(prev => ({ ...prev, ...response.data.extractedData }));
        }

        if (response.data.session) {
          setSession(prev => ({ ...prev, ...response.data.session }));
        }

        if (response.data.isComplete) {
          // Show generating report animation
          setIsGeneratingReport(true);
          
          setTimeout(async () => {
            try {
              await axios.post(`${MAVEN_API_BASE}/service-questionnaire/sessions/${sessionId}/complete`);
              setIsGeneratingReport(false);
              setIsComplete(true);
            } catch (err) {
              console.error('Error completing session:', err);
              setIsGeneratingReport(false);
              setIsComplete(true); // Still show complete even if report generation failed
            }
          }, 2000);
        }
      }
    } catch (err) {
      console.error('Error sending message:', err);
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: 'Sorry, there was an error processing your message. Please try again.' 
      }]);
    } finally {
      setIsSending(false);
      setIsTyping(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  if (isLoading) {
    return (
      <LoadingContainer>
        <LogoLoader size={200} message="Loading your questionnaire" />
      </LoadingContainer>
    );
  }

  if (error) {
    return (
      <ErrorContainer>
        <ErrorIcon><IoCloseCircle /></ErrorIcon>
        <ErrorTitle>Unable to Load Questionnaire</ErrorTitle>
        <ErrorText>{error}</ErrorText>
      </ErrorContainer>
    );
  }

  // Show generating report animation
  if (isGeneratingReport) {
    return (
      <PageWrapper theme={theme}>
        <Sidebar>
          <ServiceLogo theme={theme}>{theme.icon}</ServiceLogo>
          <ServiceName theme={theme}>{theme.name}</ServiceName>
          <ServiceSubtitle>{theme.description}</ServiceSubtitle>
          <ProgressSection>
            <ProgressLabel>Completion</ProgressLabel>
            <CircularProgress>
              <CircleSVG>
                <defs>
                  <linearGradient id="progressGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor={theme.primaryColor} />
                    <stop offset="100%" stopColor={theme.secondaryColor} />
                  </linearGradient>
                </defs>
                <CircleBackground cx="80" cy="80" r="70" />
                <CircleProgress cx="80" cy="80" r="70" progress={100} theme={theme} />
              </CircleSVG>
              <ProgressCenter>
                <ProgressPercent theme={theme}>100%</ProgressPercent>
                <ProgressText>Complete</ProgressText>
              </ProgressCenter>
            </CircularProgress>
          </ProgressSection>
        </Sidebar>
        <GeneratingContainer>
          <div style={{ marginBottom: 16 }}>
            <LogoLoader size={220} message="Generating your report" />
          </div>
          <GeneratingSubtitle>
            Please wait while we create your personalized {theme.name.toLowerCase()} report. 
            This may take a few moments...
          </GeneratingSubtitle>
          <GeneratingSteps>
            <GeneratingStep completed theme={theme}>
              <IoCheckmarkCircle /> Collecting your information
            </GeneratingStep>
            <GeneratingStep completed theme={theme}>
              <IoCheckmarkCircle /> Analyzing responses
            </GeneratingStep>
            <GeneratingStep active theme={theme}>
              <LogoLoader compact size={18} label="Creating your report" /> Creating your report
            </GeneratingStep>
            <GeneratingStep theme={theme}>
              <IoCheckmarkCircle /> Finalizing document
            </GeneratingStep>
          </GeneratingSteps>
        </GeneratingContainer>
      </PageWrapper>
    );
  }

  if (isComplete) {
    return (
      <PageWrapper theme={theme}>
        <Sidebar>
          <ServiceLogo theme={theme}>{theme.icon}</ServiceLogo>
          <ServiceName theme={theme}>{theme.name}</ServiceName>
          <ServiceSubtitle>{theme.description}</ServiceSubtitle>
          <ProgressSection>
            <ProgressLabel>Completion</ProgressLabel>
            <CircularProgress>
              <CircleSVG>
                <defs>
                  <linearGradient id="progressGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor={theme.primaryColor} />
                    <stop offset="100%" stopColor={theme.secondaryColor} />
                  </linearGradient>
                </defs>
                <CircleBackground cx="80" cy="80" r="70" />
                <CircleProgress cx="80" cy="80" r="70" progress={100} theme={theme} />
              </CircleSVG>
              <ProgressCenter>
                <ProgressPercent theme={theme}>100%</ProgressPercent>
                <ProgressText>Complete</ProgressText>
              </ProgressCenter>
            </CircularProgress>
          </ProgressSection>
        </Sidebar>
        <CompleteScreen>
          <CompleteIcon theme={theme}><IoCheckmarkCircle /></CompleteIcon>
          <CompleteTitle>Thank You!</CompleteTitle>
          <CompleteSubtitle>
            Your {theme.name.toLowerCase()} profile has been completed successfully. 
            Our team will review your information and be in touch shortly.
          </CompleteSubtitle>
          <CompleteButton theme={theme} onClick={() => window.close()}>
            Close Window
          </CompleteButton>
        </CompleteScreen>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper theme={theme}>
      <Sidebar>
        <ServiceLogo theme={theme}>{theme.icon}</ServiceLogo>
        <ServiceName theme={theme}>{theme.name}</ServiceName>
        <ServiceSubtitle>{theme.description}</ServiceSubtitle>
        
        <ProgressSection>
          <ProgressLabel>Progress</ProgressLabel>
          <CircularProgress>
            <CircleSVG>
              <defs>
                <linearGradient id="progressGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor={theme.primaryColor} />
                  <stop offset="100%" stopColor={theme.secondaryColor} />
                </linearGradient>
              </defs>
              <CircleBackground cx="80" cy="80" r="70" />
              <CircleProgress cx="80" cy="80" r="70" progress={progress.percent} theme={theme} />
            </CircleSVG>
            <ProgressCenter>
              <ProgressPercent theme={theme}>{progress.percent}%</ProgressPercent>
              <ProgressText>{progress.current}/{progress.total} Complete</ProgressText>
            </ProgressCenter>
          </CircularProgress>
        </ProgressSection>

        {Object.keys(collectedData).length > 0 && (
          <DataSection>
            <DataLabel>Your Profile</DataLabel>
            {Object.entries(collectedData).slice(0, 8).map(([key, value]) => (
              <DataItem key={key}>
                <DataKey>{key.replace(/([A-Z])/g, ' $1').trim()}</DataKey>
                <DataValue>{typeof value === 'object' ? JSON.stringify(value) : String(value)}</DataValue>
              </DataItem>
            ))}
          </DataSection>
        )}
        
        {uploadedDocuments.length > 0 && (
          <UploadedDocumentsSection>
            <DataLabel>Uploaded Documents</DataLabel>
            {uploadedDocuments.map((doc, index) => (
              <UploadedDocItem key={index}>
                <DocIcon theme={theme}>{getFileIcon(doc.name)}</DocIcon>
                <DocInfo>
                  <DocName>{doc.name}</DocName>
                  <DocMeta>{formatFileSize(doc.size)} • {doc.uploadedAt}</DocMeta>
                </DocInfo>
              </UploadedDocItem>
            ))}
          </UploadedDocumentsSection>
        )}
      </Sidebar>

      <ChatContainer>
        <ChatHeader>
          <ChatHeaderIcon theme={theme}><HiOutlineSparkles /></ChatHeaderIcon>
          <ChatHeaderInfo>
            <ChatHeaderTitle>AI Assistant</ChatHeaderTitle>
            <ChatHeaderSubtitle>I'll help you complete your {theme.name.toLowerCase()} profile</ChatHeaderSubtitle>
          </ChatHeaderInfo>
        </ChatHeader>

        <MessagesContainer>
          {messages.map((msg, index) => (
            <MessageWrapper key={index} isUser={msg.role === 'user'}>
              <MessageAvatar isUser={msg.role === 'user'} theme={theme}>
                {msg.role === 'user' ? <FaUser /> : <FaRobot />}
              </MessageAvatar>
              <MessageBubble 
                isUser={msg.role === 'user'} 
                theme={theme}
                dangerouslySetInnerHTML={{ 
                  __html: DOMPurify.sanitize(
                    // Remove [PROGRESS:X/Y] tag from displayed message
                    msg.content.replace(/\s*\[PROGRESS:\d+\/\d+\]\s*/g, ''), 
                    { 
                      ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'ul', 'ol', 'li', 'b', 'i', 'span', 'div', 'h1', 'h2', 'h3', 'h4', 'a', 'table', 'thead', 'tbody', 'tr', 'th', 'td', 'pre', 'code', 'hr', 'blockquote'],
                      ALLOWED_ATTR: ['href', 'target', 'style', 'class', 'colspan', 'rowspan']
                    }
                  ) 
                }}
              />
            </MessageWrapper>
          ))}
          
          {isTyping && (
            <MessageWrapper isUser={false}>
              <MessageAvatar isUser={false} theme={theme}><FaRobot /></MessageAvatar>
              <TypingIndicator theme={theme}>
                <LogoLoader inline size={18} phrases={['Thinking', 'Reading your answer', 'Preparing the next question']} />
              </TypingIndicator>
            </MessageWrapper>
          )}
          
          <div ref={messagesEndRef} />
        </MessagesContainer>

        <InputArea>
          {selectedFiles.length > 0 && !isUploadingFile && (
            <FilePreviewContainer>
              {selectedFiles.map((file, index) => (
                <FilePreviewItem key={index}>
                  <FileIcon theme={theme}>{getFileIcon(file.name)}</FileIcon>
                  <FileName>{file.name}</FileName>
                  <RemoveFileButton onClick={() => removeSelectedFile(index)}>
                    <IoCloseCircle size={16} />
                  </RemoveFileButton>
                </FilePreviewItem>
              ))}
            </FilePreviewContainer>
          )}
          
          {isUploadingFile && (
            <FilePreviewContainer style={{ position: 'relative', minHeight: '120px' }}>
              <UploadingOverlay>
                <LogoLoader size={110} message="Uploading your files" />
                <UploadingSubtitle style={{ marginTop: 8, marginBottom: 0 }}>Processing and extracting data...</UploadingSubtitle>
              </UploadingOverlay>
            </FilePreviewContainer>
          )}

          {isRecording && (
            <RecordingIndicator>
              <RecordingDot />
              <span>Recording</span>
              <span>{formatTime(recordingTime)}</span>
            </RecordingIndicator>
          )}
          
          {isTranscribing && (
            <TranscribingStatus>
              <LogoLoader compact size={12} label="Transcribing" />
              <span>{transcriptionStatus || 'Transcribing...'}</span>
            </TranscribingStatus>
          )}
          
          {!isRecording && !isTranscribing && transcriptionStatus && (
            <TranscribingStatus><span>{transcriptionStatus}</span></TranscribingStatus>
          )}

          <InputWrapper focused={inputFocused} theme={theme}>
            <InputActions>
              <ActionButton
                onClick={() => fileInputRef.current?.click()}
                disabled={isSending || isUploadingFile}
                title="Upload files (resume, documents)"
              >
                {isUploadingFile ? <LogoLoader compact size={16} label="Uploading" /> : <IoAttach />}
              </ActionButton>
              
              <ActionButton
                isRecording={isRecording}
                onClick={toggleRecording}
                disabled={isSending || isTranscribing}
                title={isRecording ? 'Stop recording' : 'Start voice input'}
              >
                {isRecording ? <IoStop /> : <IoMic />}
              </ActionButton>
            </InputActions>
            
            <TextInput
              ref={inputRef}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              onFocus={() => setInputFocused(true)}
              onBlur={() => setInputFocused(false)}
              placeholder={isRecording ? "Recording... speak now" : "Type your response or use voice input..."}
              rows={1}
              disabled={isSending || isRecording}
            />
            
            <SendButton 
              theme={theme}
              disabled={(!inputValue.trim() && selectedFiles.length === 0) || isSending || isRecording}
              onClick={handleSendMessage}
            >
              {isSending ? <LogoLoader compact size={18} onDark label="Sending" /> : <IoSend />}
            </SendButton>
          </InputWrapper>
          
          <HiddenFileInput
            ref={fileInputRef}
            type="file"
            multiple
            accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png,.gif,.xlsx,.xls,.csv"
            onChange={handleFileSelect}
          />
        </InputArea>
      </ChatContainer>
    </PageWrapper>
  );
};

export default ServiceQuestionnairePage;
